/**
 * GearEditModal Component
 *
 * Modal for creating/editing gear with 4 tabs:
 * - Tab 1: Basic Info (gear_name, gear_category, gear_subcategory, item_type, descriptions)
 * - Tab 2: Details (rental info, cost, specifications, seasonal, brands)
 * - Tab 3: Categories (manage L1/L2/L3 associations)
 * - Tab 4: Media (image upload/URL)
 *
 * Epic: Super Admin Catalog Management Console
 * Story: SCRUM-301
 */

import { useState, useEffect, useCallback } from 'react';
import { X, Loader, Image as ImageIcon, Plus, Trash } from 'lucide-react';
import { gearCatalogAdminApi } from '../../services/catalogAdminApi';
import FormField from './FormField';
import ConfirmModal from '../shared/ConfirmModal';

// Gear category options
const GEAR_CATEGORY_OPTIONS = [
  'Personal Gear',
  'Technical Equipment',
  'Safety Equipment',
  'Navigation',
  'Shelter & Sleeping',
  'Food & Water',
  'Clothing',
  'Transportation',
  'Specialized Equipment',
  'Other'
];

// Seasons for seasonal gear
const SEASONS = ['Spring', 'Summer', 'Fall', 'Winter'];

// Weather conditions
const WEATHER_CONDITIONS = [
  'Rain',
  'Snow',
  'Wind',
  'Cold',
  'Heat',
  'Sun',
  'Humidity'
];

export default function GearEditModal({ gear, isCreating, onSave: _onSave, onClose }) {
  // Tab state
  const [activeTab, setActiveTab] = useState('basic');

  // Form data
  const [formData, setFormData] = useState({
    gear_name: '',
    gear_category: 'Personal Gear',
    gear_subcategory: '',
    item_type: 'gear',
    short_description: '',
    description: '',
    commonly_available_for_rental: false,
    rental_cost_per_day_usd: '',
    typical_cost_usd: '',
    cost_range_min_usd: '',
    cost_range_max_usd: '',
    requires_certification: false,
    certification_type: '',
    size_variants: false,
    weight_ounces: '',
    seasonal_gear: false,
    seasons: [],
    weather_conditions: [],
    brand_names: [],
    is_active: true
  });

  // Category associations
  const [categoryAssociations, setCategoryAssociations] = useState([]);
  const [l1Categories, setL1Categories] = useState([]);
  const [l2Categories, setL2Categories] = useState([]);
  const [l3Categories, setL3Categories] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    category_l1_id: '',
    category_l2_id: '',
    category_l3_id: ''
  });

  // Image upload state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // UI state
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, action: null, data: null, message: '' });

  /**
   * Load full gear data with associations
   */
  const loadGearData = useCallback(async () => {
    setLoadingData(true);
    try {
      const response = await gearCatalogAdminApi.getGearById(gear.id);
      const gearData = response.data;

      setFormData({
        gear_name: gearData.gear_name || '',
        gear_category: gearData.gear_category || 'Personal Gear',
        gear_subcategory: gearData.gear_subcategory || '',
        item_type: gearData.item_type || 'gear',
        short_description: gearData.short_description || '',
        description: gearData.description || '',
        commonly_available_for_rental: gearData.commonly_available_for_rental || false,
        rental_cost_per_day_usd: gearData.rental_cost_per_day_usd || '',
        typical_cost_usd: gearData.typical_cost_usd || '',
        cost_range_min_usd: gearData.cost_range_min_usd || '',
        cost_range_max_usd: gearData.cost_range_max_usd || '',
        requires_certification: gearData.requires_certification || false,
        certification_type: gearData.certification_type || '',
        size_variants: gearData.size_variants || false,
        weight_ounces: gearData.weight_ounces || '',
        seasonal_gear: gearData.seasonal_gear || false,
        seasons: gearData.seasons || [],
        weather_conditions: gearData.weather_conditions || [],
        brand_names: gearData.brand_names || [],
        photo_url: gearData.photo_url || '',
        is_active: gearData.is_active !== undefined ? gearData.is_active : true
      });

      // Load category associations
      if (gearData.category_associations) {
        setCategoryAssociations(gearData.category_associations);
      }
    } catch (err) {
      console.error('Failed to load gear data:', err);
      setErrors({ general: 'Failed to load gear data' });
    } finally {
      setLoadingData(false);
    }
  }, [gear]);

  /**
   * Load gear data when editing
   */
  useEffect(() => {
    if (!isCreating && gear && gear.id) {
      loadGearData();
    } else if (isCreating && gear) {
      // Set default values from passed gear object
      setFormData(prev => ({ ...prev, ...gear }));
    }
  }, [gear, isCreating, loadGearData]);

  /**
   * Handle field change
   */
  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);

    // Clear error for this field
    // eslint-disable-next-line security/detect-object-injection -- safe: field is validated form field name
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        // eslint-disable-next-line security/detect-object-injection -- safe: field is validated form field name
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  /**
   * Handle array field change (seasons, weather_conditions, brand_names)
   */
  const handleArrayToggle = (field, value) => {
    setFormData(prev => {
      // eslint-disable-next-line security/detect-object-injection -- safe: field is validated form field name
      const currentArray = prev[field] || [];
      const newArray = currentArray.includes(value)
        ? currentArray.filter(v => v !== value)
        : [...currentArray, value];
      return { ...prev, [field]: newArray };
    });
    setHasUnsavedChanges(true);
  };

  /**
   * Handle brand names input (comma-separated)
   */
  const handleBrandNamesChange = (value) => {
    const brands = value.split(',').map(b => b.trim()).filter(b => b);
    handleFieldChange('brand_names', brands);
  };

  /**
   * Validate form
   */
  const validateForm = () => {
    const newErrors = {};

    if (!formData.gear_name || formData.gear_name.trim() === '') {
      newErrors.gear_name = 'Gear name is required';
    }

    if (!formData.gear_category) {
      newErrors.gear_category = 'Gear category is required';
    }

    if (!formData.item_type) {
      newErrors.item_type = 'Item type is required';
    }

    // Validate cost fields if provided
    if (formData.typical_cost_usd) {
      const cost = parseFloat(formData.typical_cost_usd);
      if (isNaN(cost) || cost < 0) {
        newErrors.typical_cost_usd = 'Must be a valid positive number';
      }
    }

    if (formData.rental_cost_per_day_usd) {
      const cost = parseFloat(formData.rental_cost_per_day_usd);
      if (isNaN(cost) || cost < 0) {
        newErrors.rental_cost_per_day_usd = 'Must be a valid positive number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle save
   */
  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      await _onSave(formData);
      setHasUnsavedChanges(false);
    } catch (err) {
      setErrors({
        general: err.message || 'Failed to save gear'
      });
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Handle close
   */
  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setConfirmModal({
        isOpen: true,
        action: 'close',
        data: null,
        message: 'You have unsaved changes. Are you sure you want to close?'
      });
    } else {
      onClose();
    }
  }, [hasUnsavedChanges, onClose]);

  /**
   * Handle escape key
   */
  useEffect(() => {
    const handleEscape = (  _e) => {
      if (_e.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [handleClose]);

  /**
   * Lock body scroll when modal open
   */
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  /**
   * Category Association Handlers
   */
  const handleAddCategoryAssociation = async () => {
    setEditingCategory({ isNew: true });
    setCategoryForm({
      category_l1_id: '',
      category_l2_id: '',
      category_l3_id: ''
    });

    // Load L1 categories from taxonomy API
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3030'}/api/v1/taxonomy/categories`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setL1Categories(data.data);
      }
    } catch (err) {
      console.error('Failed to load L1 categories:', err);
    }
  };

  const handleSaveCategoryAssociation = async () => {
    if (!categoryForm.category_l1_id) {
      setErrors(prev => ({ ...prev, general: 'Please select at least L1 category' }));
      setTimeout(() => setErrors(prev => ({ ...prev, general: null })), 3000);
      return;
    }

    try {
      const response = await gearCatalogAdminApi.addCategoryAssociation(gear.id, categoryForm);
      setCategoryAssociations(prev => [...prev, response.data]);
      setEditingCategory(null);
      setHasUnsavedChanges(true);
    } catch (err) {
      console.error('Failed to add category association:', err);
      setErrors(prev => ({ ...prev, general: 'Failed to add category association' }));
      setTimeout(() => setErrors(prev => ({ ...prev, general: null })), 3000);
    }
  };

  const handleDeleteCategoryAssociation = async (associationId) => {
    setConfirmModal({
      isOpen: true,
      action: 'deleteCategory',
      data: associationId,
      message: 'Remove this category association?'
    });
  };

  const executeDeleteCategoryAssociation = async (associationId) => {

    try {
      await gearCatalogAdminApi.deleteCategoryAssociation(gear.id, associationId);
      setCategoryAssociations(prev => prev.filter(ca => ca.id !== associationId));
      setHasUnsavedChanges(true);
    } catch (err) {
      console.error('Failed to delete category association:', err);
      setErrors(prev => ({ ...prev, general: 'Failed to delete category association' }));
      setTimeout(() => setErrors(prev => ({ ...prev, general: null })), 3000);
    }
  };

  const handleCancelCategoryEdit = () => {
    setEditingCategory(null);
    setCategoryForm({
      category_l1_id: '',
      category_l2_id: '',
      category_l3_id: ''
    });
    setL2Categories([]);
    setL3Categories([]);
  };

  /**
   * Load L2 categories for selected L1
   */
  const loadL2Categories = async (l1Id) => {
    if (!l1Id) {
      setL2Categories([]);
      return;
    }

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:3030'}/api/v1/taxonomy/categories/l2?parentId=${l1Id}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      );
      const data = await response.json();
      if (data.success) {
        setL2Categories(data.data);
      }
    } catch (err) {
      console.error('Failed to load L2 categories:', err);
    }
  };

  /**
   * Load L3 categories for selected L2
   */
  const loadL3Categories = async (l2Id) => {
    if (!l2Id) {
      setL3Categories([]);
      return;
    }

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:3030'}/api/v1/taxonomy/categories/l3?parentId=${l2Id}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      );
      const data = await response.json();
      if (data.success) {
        setL3Categories(data.data);
      }
    } catch (err) {
      console.error('Failed to load L3 categories:', err);
    }
  };

  /**
   * Image Upload Handler
   */
  const handleImageUpload = async (  _e) => {
    const file = _e.target.files?.[0];
    if (!file) {return;}

    // Validate file
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setErrors(prev => ({ ...prev, general: 'File size must be less than 5MB' }));
      setTimeout(() => setErrors(prev => ({ ...prev, general: null })), 3000);
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setErrors(prev => ({ ...prev, general: 'File must be JPG, PNG, or WebP' }));
      setTimeout(() => setErrors(prev => ({ ...prev, general: null })), 3000);
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    // Simulate progress
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => Math.min(prev + 10, 90));
    }, 200);

    try {
      const response = await gearCatalogAdminApi.uploadImage(gear.id, file);
      setUploadProgress(100);

      // Update form data with new image URL
      setFormData(prev => ({
        ...prev,
        photo_url: response.data.imageUrl
      }));

      setHasUnsavedChanges(true);
    } catch (err) {
      console.error('Failed to upload image:', err);
      setErrors(prev => ({ ...prev, general: 'Failed to upload image' }));
      setTimeout(() => setErrors(prev => ({ ...prev, general: null })), 3000);
    } finally {
      clearInterval(progressInterval);
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="relative w-full max-w-4xl bg-white rounded-lg shadow-xl transform transition-all"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900">
              {isCreating ? 'Add New Gear' : `Edit: ${formData.gear_name || 'Gear'}`}
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Loading State */}
          {loadingData && (
            <div className="px-6 py-12 text-center">
              <Loader className="w-8 h-8 animate-spin mx-auto text-blue-600" />
              <p className="mt-4 text-gray-600">Loading gear data...</p>
            </div>
          )}

          {/* Form Content */}
          {!loadingData && (
            <>
              {/* Tabs */}
              <div className="border-b border-gray-200">
                <nav className="flex -mb-px px-6">
                  <button
                    onClick={() => setActiveTab('basic')}
                    className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'basic'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Basic Info
                  </button>
                  <button
                    onClick={() => setActiveTab('details')}
                    className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'details'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Details
                  </button>
                  <button
                    onClick={() => setActiveTab('categories')}
                    className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'categories'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Categories
                  </button>
                  <button
                    onClick={() => setActiveTab('media')}
                    className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'media'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Media
                  </button>
                </nav>
              </div>

              {/* Tab Content */}
              <div className="px-6 py-6 max-h-[60vh] overflow-y-auto">
                {/* General Error */}
                {errors.general && (
                  <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm text-red-800">{errors.general}</p>
                  </div>
                )}

                {/* Tab 1: Basic Info */}
                {activeTab === 'basic' && (
                  <div className="space-y-6">
                    <FormField
                      label="Gear Name"
                      name="gear_name"
                      type="text"
                      value={formData.gear_name}
                      onChange={(value) => handleFieldChange('gear_name', value)}
                      error={errors.gear_name}
                      required
                      placeholder="e.g., Backpack, Tent, Fishing Rod"
                    />

                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        label="Gear Category"
                        name="gear_category"
                        type="select"
                        value={formData.gear_category}
                        onChange={(value) => handleFieldChange('gear_category', value)}
                        error={errors.gear_category}
                        required
                        options={GEAR_CATEGORY_OPTIONS.map(cat => ({ value: cat, label: cat }))}
                      />

                      <FormField
                        label="Gear Subcategory"
                        name="gear_subcategory"
                        type="text"
                        value={formData.gear_subcategory}
                        onChange={(value) => handleFieldChange('gear_subcategory', value)}
                        placeholder="e.g., Day Pack, 3-Season"
                      />
                    </div>

                    <FormField
                      label="Item Type"
                      name="item_type"
                      type="select"
                      value={formData.item_type}
                      onChange={(value) => handleFieldChange('item_type', value)}
                      error={errors.item_type}
                      required
                      options={[
                        { value: 'gear', label: 'Gear' },
                        { value: 'other', label: 'Other' }
                      ]}
                    />

                    <FormField
                      label="Short Description"
                      name="short_description"
                      type="textarea"
                      rows={2}
                      value={formData.short_description}
                      onChange={(value) => handleFieldChange('short_description', value)}
                      placeholder="Brief one-line description (100 chars max)"
                      helperText="Used in lists and previews"
                    />

                    <FormField
                      label="Full Description"
                      name="description"
                      type="textarea"
                      rows={4}
                      value={formData.description}
                      onChange={(value) => handleFieldChange('description', value)}
                      placeholder="Detailed description of the gear item"
                      helperText="Detailed information about use cases, features, etc."
                    />
                  </div>
                )}

                {/* Tab 2: Details - TO BE IMPLEMENTED */}
                {activeTab === 'details' && (
                  <div className="space-y-6">
                    {/* Rental Information */}
                    <div className="border-b border-gray-200 pb-4">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Rental Information</h3>

                      <div className="mb-4">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.commonly_available_for_rental}
                            onChange={(e) => handleFieldChange('commonly_available_for_rental', e.target.checked)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            Commonly available for rental
                          </span>
                        </label>
                      </div>

                      {formData.commonly_available_for_rental && (
                        <FormField
                          label="Rental Cost per Day (USD)"
                          name="rental_cost_per_day_usd"
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.rental_cost_per_day_usd}
                          onChange={(value) => handleFieldChange('rental_cost_per_day_usd', value)}
                          error={errors.rental_cost_per_day_usd}
                          placeholder="25.00"
                        />
                      )}
                    </div>

                    {/* Cost Information */}
                    <div className="border-b border-gray-200 pb-4">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Cost Information</h3>

                      <FormField
                        label="Typical Cost (USD)"
                        name="typical_cost_usd"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.typical_cost_usd}
                        onChange={(value) => handleFieldChange('typical_cost_usd', value)}
                        error={errors.typical_cost_usd}
                        placeholder="150.00"
                        helperText="Average price for this gear"
                      />

                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <FormField
                          label="Cost Range Min (USD)"
                          name="cost_range_min_usd"
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.cost_range_min_usd}
                          onChange={(value) => handleFieldChange('cost_range_min_usd', value)}
                          placeholder="100.00"
                        />

                        <FormField
                          label="Cost Range Max (USD)"
                          name="cost_range_max_usd"
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.cost_range_max_usd}
                          onChange={(value) => handleFieldChange('cost_range_max_usd', value)}
                          placeholder="200.00"
                        />
                      </div>
                    </div>

                    {/* Specifications */}
                    <div className="border-b border-gray-200 pb-4">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Specifications</h3>

                      <div className="space-y-4">
                        <div>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={formData.requires_certification}
                              onChange={(e) => handleFieldChange('requires_certification', e.target.checked)}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="ml-2 text-sm text-gray-700">
                              Requires certification
                            </span>
                          </label>
                        </div>

                        {formData.requires_certification && (
                          <FormField
                            label="Certification Type"
                            name="certification_type"
                            type="text"
                            value={formData.certification_type}
                            onChange={(value) => handleFieldChange('certification_type', value)}
                            placeholder="e.g., Avalanche Safety, Scuba Diving"
                          />
                        )}

                        <div>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={formData.size_variants}
                              onChange={(e) => handleFieldChange('size_variants', e.target.checked)}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="ml-2 text-sm text-gray-700">
                              Available in different sizes
                            </span>
                          </label>
                        </div>

                        <FormField
                          label="Weight (ounces)"
                          name="weight_ounces"
                          type="number"
                          min="0"
                          step="0.1"
                          value={formData.weight_ounces}
                          onChange={(value) => handleFieldChange('weight_ounces', value)}
                          placeholder="32.0"
                          helperText="Weight in ounces (1 lb = 16 oz)"
                        />
                      </div>
                    </div>

                    {/* Seasonal & Weather */}
                    <div className="border-b border-gray-200 pb-4">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Seasonal & Weather</h3>

                      <div className="mb-4">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.seasonal_gear}
                            onChange={(e) => handleFieldChange('seasonal_gear', e.target.checked)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            Seasonal gear (specific to certain seasons)
                          </span>
                        </label>
                      </div>

                      {formData.seasonal_gear && (
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Seasons
                          </label>
                          <div className="grid grid-cols-4 gap-2">
                            {SEASONS.map(season => (
                              <label key={season} className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={formData.seasons.includes(season)}
                                  onChange={() => handleArrayToggle('seasons', season)}
                                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <span className="ml-2 text-sm text-gray-700">{season}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Weather Conditions
                        </label>
                        <div className="grid grid-cols-4 gap-2">
                          {WEATHER_CONDITIONS.map(condition => (
                            <label key={condition} className="flex items-center">
                              <input
                                type="checkbox"
                                checked={formData.weather_conditions.includes(condition)}
                                onChange={() => handleArrayToggle('weather_conditions', condition)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <span className="ml-2 text-sm text-gray-700">{condition}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Brand Names */}
                    <div>
                      <FormField
                        label="Brand Names"
                        name="brand_names"
                        type="text"
                        value={formData.brand_names.join(', ')}
                        onChange={handleBrandNamesChange}
                        placeholder="Patagonia, REI, The North Face"
                        helperText="Comma-separated list of popular brands"
                      />
                    </div>
                  </div>
                )}

                {/* Tab 3: Categories - TO BE IMPLEMENTED */}
                {activeTab === 'categories' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-gray-900">
                        Activity Category Associations
                      </h3>
                      <button
                        onClick={handleAddCategoryAssociation}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                        disabled={isCreating}
                        title={isCreating ? 'Save gear first before adding categories' : 'Add category association'}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Category
                      </button>
                    </div>

                    {isCreating && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <p className="text-sm text-yellow-800">
                          Save the gear item first before managing category associations.
                        </p>
                      </div>
                    )}

                    {!isCreating && categoryAssociations.length === 0 && (
                      <div className="text-center py-12">
                        <p className="text-gray-500">No category associations yet</p>
                      </div>
                    )}

                    {!isCreating && categoryAssociations.length > 0 && (
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              L1 Category
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              L2 Category
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              L3 Category
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {categoryAssociations.map(ca => (
                            <tr key={ca.id}>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {ca.category_l1_name || '—'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {ca.category_l2_name || '—'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {ca.category_l3_name || '—'}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <button
                                  onClick={() => handleDeleteCategoryAssociation(ca.id)}
                                  className="text-red-600 hover:text-red-900"
                                  title="Delete association"
                                >
                                  <Trash className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}

                    {/* Category Association Form Modal */}
                    {editingCategory && (
                      <div className="fixed inset-0 z-50 overflow-y-auto">
                        <div className="flex min-h-screen items-center justify-center p-4">
                          <div className="fixed inset-0 bg-black bg-opacity-30" onClick={handleCancelCategoryEdit} />
                          <div className="relative bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">
                              Add Category Association
                            </h3>

                            <div className="space-y-4">
                              <FormField
                                label="L1 Category"
                                name="category_l1_id"
                                type="select"
                                value={categoryForm.category_l1_id}
                                onChange={(value) => {
                                  setCategoryForm(prev => ({ ...prev, category_l1_id: value, category_l2_id: '', category_l3_id: '' }));
                                  loadL2Categories(value);
                                }}
                                required
                                options={[
                                  { value: '', label: 'Select L1 Category' },
                                  ...l1Categories.map(cat => ({ value: cat.id, label: cat.name }))
                                ]}
                              />

                              {categoryForm.category_l1_id && (
                                <FormField
                                  label="L2 Category (Optional)"
                                  name="category_l2_id"
                                  type="select"
                                  value={categoryForm.category_l2_id}
                                  onChange={(value) => {
                                    setCategoryForm(prev => ({ ...prev, category_l2_id: value, category_l3_id: '' }));
                                    loadL3Categories(value);
                                  }}
                                  options={[
                                    { value: '', label: 'Select L2 Category' },
                                    ...l2Categories.map(cat => ({ value: cat.id, label: cat.name }))
                                  ]}
                                />
                              )}

                              {categoryForm.category_l2_id && (
                                <FormField
                                  label="L3 Category (Optional)"
                                  name="category_l3_id"
                                  type="select"
                                  value={categoryForm.category_l3_id}
                                  onChange={(value) => setCategoryForm(prev => ({ ...prev, category_l3_id: value }))}
                                  options={[
                                    { value: '', label: 'Select L3 Category' },
                                    ...l3Categories.map(cat => ({ value: cat.id, label: cat.name }))
                                  ]}
                                />
                              )}
                            </div>

                            <div className="mt-6 flex justify-end gap-3">
                              <button
                                onClick={handleCancelCategoryEdit}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={handleSaveCategoryAssociation}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                              >
                                Add Association
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Tab 4: Media */}
                {activeTab === 'media' && (
                  <div className="space-y-6">
                    {/* Current Image */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Current Image
                      </label>
                      {formData.photo_url ? (
                        <div className="relative inline-block">
                          <img
                            src={formData.photo_url}
                            alt={formData.gear_name}
                            className="w-64 h-64 object-cover rounded-lg border border-gray-300"
                          />
                          <button
                            onClick={() => handleFieldChange('photo_url', '')}
                            className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700"
                            title="Remove image"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="w-64 h-64 bg-gray-100 rounded-lg border border-gray-300 flex items-center justify-center">
                          <ImageIcon className="w-16 h-16 text-gray-400" />
                        </div>
                      )}
                    </div>

                    {/* Upload New Image */}
                    {!isCreating && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Upload New Image
                        </label>
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp"
                          onChange={handleImageUpload}
                          disabled={uploading}
                          className="block w-full text-sm text-gray-500
                            file:mr-4 file:py-2 file:px-4
                            file:rounded-md file:border-0
                            file:text-sm file:font-medium
                            file:bg-blue-50 file:text-blue-700
                            hover:file:bg-blue-100
                            disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Max 5MB. Formats: JPG, PNG, WebP
                        </p>

                        {/* Upload Progress */}
                        {uploading && uploadProgress > 0 && uploadProgress < 100 && (
                          <div className="mt-2">
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${uploadProgress}%` }}
                              />
                            </div>
                            <p className="text-xs text-gray-600 mt-1">{uploadProgress}% uploaded</p>
                          </div>
                        )}
                      </div>
                    )}

                    {isCreating && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <p className="text-sm text-yellow-800">
                          Save the gear item first before uploading images.
                        </p>
                      </div>
                    )}

                    {/* Manual URL Entry */}
                    <div>
                      <FormField
                        label="Image URL (Alternative)"
                        name="photo_url"
                        type="url"
                        value={formData.photo_url}
                        onChange={(value) => handleFieldChange('photo_url', value)}
                        placeholder="https://..."
                        helperText="Enter URL if not uploading a file"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="text-sm text-gray-600">
                  {hasUnsavedChanges && (
                    <span className="text-yellow-600">● Unsaved changes</span>
                  )}
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isSaving || !hasUnsavedChanges}
                  >
                    {isSaving && <Loader className="w-4 h-4 mr-2 animate-spin" />}
                    {isCreating ? 'Create Gear' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, action: null, data: null, message: '' })}
        onConfirm={async () => {
          const { action, data } = confirmModal;
          setConfirmModal({ isOpen: false, action: null, data: null, message: '' });

          if (action === 'close') {
            onClose();
          } else if (action === 'deleteCategory') {
            await executeDeleteCategoryAssociation(data);
          }
        }}
        title="Confirm Action"
        message={confirmModal.message}
        confirmText="Confirm"
        cancelText="Cancel"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
    </div>
  );
}
